import React, { useEffect, useState } from 'react';
import {
  Alert, Card, Descriptions, Tag, Timeline, Row, Col, Button, Space, Typography, Spin, message, Select, Input, Popconfirm, Modal,
} from 'antd';
import {
  ArrowLeftOutlined, DownloadOutlined, EditOutlined,
  CheckCircleOutlined, WarningOutlined, DownOutlined, UpOutlined, DeleteOutlined, ExclamationCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getVersionDetail,
  updateVersion,
  getVersionReleaseNote,
  getGitLabRelease,
  downloadVersionFile,
  downloadGitLabVersionFile,
  downloadWelzekViewerAttachment,
  addVersionRemark,
  deleteVersionRemark,
  shareVersionSummaryToFeishu,
} from '../services/api';
import { StatusBadge, ReleaseTypeBadge } from '../components/StatusBadge';
import { formatFileSize, GITLAB_PRODUCTS, getProductDisplayName } from '../utils/format';
import { groupFeaturesByIssue } from '../utils/features';
import { getFirmwareVersionEntries, getMarketVersionInfo } from '../utils/versionInfo';
import dayjs from 'dayjs';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

export default function VersionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_super_admin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyNote, setDailyNote] = useState({ loading: false, error: false, sections: [], delta: null, textQuality: null });
  const [gitLabInfo, setGitLabInfo] = useState(null);
  const [changesCollapsed, setChangesCollapsed] = useState(true);
  const [dailyDeltaCollapsed, setDailyDeltaCollapsed] = useState(true);
  const [remarkText, setRemarkText] = useState('');
  const [remarkSubmitting, setRemarkSubmitting] = useState(false);
  const [remarkDeletingId, setRemarkDeletingId] = useState(null);
  const [summaryFeishuSending, setSummaryFeishuSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    setChangesCollapsed(true);
    setDailyDeltaCollapsed(true);
    setRemarkText('');
    setRemarkSubmitting(false);
    setRemarkDeletingId(null);
    setSummaryFeishuSending(false);
    getVersionDetail(id).then(res => {
      const d = res.data.data;
      setData(d);
      setLoading(false);

      if (d.status === 'released' && GITLAB_PRODUCTS.includes(d.product_model)) {
        getGitLabRelease(d.id).then(glRes => {
          setGitLabInfo(glRes.data.data);
        }).catch(() => {
          setGitLabInfo(null);
        });
      }
      if (d.version_type === 'daily') {
        setDailyNote({ loading: true, error: false, sections: [], delta: null, textQuality: null });
        getVersionReleaseNote(id).then(rnRes => {
          const rn = rnRes.data.data;
          setDailyNote({
            loading: false,
            error: false,
            sections: rn.sections || [],
            delta: rn.delta || null,
            textQuality: rn.textQuality || null,
          });
          // 若发布说明中包含版本信息，更新到 data 让固件组件版本卡片显示
          if (rn.versionInfo && Object.keys(rn.versionInfo).length > 0) {
            setData(prev => ({
              ...prev,
              version_info: JSON.stringify({
                ...rn.versionInfo,
                uiVersion: rn.uiVersion || undefined,
              }),
            }));
          }
        }).catch(() => {
          setDailyNote({ loading: false, error: true, sections: [], delta: null, textQuality: null });
        });
      }
    }).catch(() => {
      message.error('版本不存在');
      navigate('/versions');
    });
  }, [id, navigate]);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!data) return null;

  const isMarketVersion = data.version_type === 'market';
  const marketInfo = getMarketVersionInfo(data);
  const productDisplayName = getProductDisplayName(data.product_model);
  const displayVersionTitle = isMarketVersion ? data.version : `v${data.version}`;
  const hasLocalFile = Boolean(data.file_path && data.file_name);
  const primaryGitLabAsset = gitLabInfo?.assets?.[0] || null;
  const displayFileName = hasLocalFile ? data.file_name : primaryGitLabAsset?.name;
  const displayFileSize = hasLocalFile ? data.file_size : primaryGitLabAsset?.size;
  const welzekViewerAttachment = data.welzek_viewer_attachment || null;
  const defaultChangeCounts = { new_feature: 0, improvement: 0, bugfix: 0 };
  const dailyDelta = dailyNote.delta;
  const dailyTextQuality = dailyDelta?.textQuality?.sourceCorrupted ? dailyDelta.textQuality : dailyNote.textQuality;
  const dailyDeltaSections = dailyDelta?.sections || [];
  const dailyDeltaCount = dailyDelta ? dailyDelta.total : 0;
  const dailyStatsCounts = dailyDelta?.counts || defaultChangeCounts;
  const releaseStatsCounts = {
    new_feature: data.features.filter(f => f.type === 'new_feature').length,
    improvement: data.features.filter(f => f.type === 'improvement').length,
    bugfix: data.features.filter(f => f.type === 'bugfix').length,
  };
  const statsTypeCounts = data.version_type === 'daily' ? dailyStatsCounts : releaseStatsCounts;
  const dailyChangeCount = dailyNote.sections.reduce((sum, s) => sum + (s.unchanged ? 0 : s.entries.length), 0);
  const changeRecordCount = data.version_type === 'daily' ? dailyChangeCount : data.features.length;
  const statsChangeCount = data.version_type === 'daily' ? dailyDeltaCount : data.features.length;
  const changeRecordTitle = `变更记录 (${data.version_type === 'daily' ? (changeRecordCount || '发布说明') : changeRecordCount})`;
  const versionRemarks = data.remarks || [];
  const canDeleteRemark = (remark) => isAdmin || Number(remark.user_id) === Number(user?.id);
  const hasVersionRemarksForDownload = versionRemarks.filter((remark) => String(remark.content || '').trim()).length > 0;
  const renderMarketValue = (value) => {
    if (value === undefined || value === null) return '--';
    const text = String(value).trim();
    return text || '--';
  };

  const handleAddRemark = async () => {
    const content = remarkText.trim();
    if (!content) {
      message.warning('请输入备注内容');
      return;
    }
    setRemarkSubmitting(true);
    try {
      const res = await addVersionRemark(id, { content });
      const remark = res.data.data;
      setData(prev => ({
        ...prev,
        remarks: [remark, ...(prev.remarks || [])],
      }));
      setRemarkText('');
      message.success('备注已添加');
    } catch (err) {
      message.error(err.response?.data?.message || '添加备注失败');
    } finally {
      setRemarkSubmitting(false);
    }
  };

  const handleDeleteRemark = async (remarkId) => {
    setRemarkDeletingId(remarkId);
    try {
      await deleteVersionRemark(id, remarkId);
      setData(prev => ({
        ...prev,
        remarks: (prev.remarks || []).filter(item => item.id !== remarkId),
      }));
      message.success('备注已删除');
    } catch (err) {
      message.error(err.response?.data?.message || '删除备注失败');
    } finally {
      setRemarkDeletingId(null);
    }
  };

  const handleStatusChange = (newStatus) => {
    updateVersion(id, { status: newStatus }).then(() => {
      setData({ ...data, status: newStatus });
      message.success(`状态已更新为: ${{ draft: '草稿', released: '已发布' }[newStatus] || newStatus}`);
    });
  };

  const handleShareVersionSummaryToFeishu = () => {
    Modal.confirm({
      title: '发送版本摘要到飞书？',
      icon: <ExclamationCircleOutlined />,
      content: `将发送 ${isMarketVersion ? (marketInfo?.productModel || productDisplayName) : productDisplayName} ${displayVersionTitle} 的版本摘要卡片到配置的飞书群。`,
      okText: '确认发送',
      cancelText: '取消',
      onOk: async () => {
        setSummaryFeishuSending(true);
        try {
          const res = await shareVersionSummaryToFeishu(id);
          message.success(res.data?.message || '版本摘要已发送到飞书');
        } catch (err) {
          message.error(err.response?.data?.message || '版本摘要发送到飞书失败');
        } finally {
          setSummaryFeishuSending(false);
        }
      },
    });
  };

  const showVersionRemarksBeforeDownload = async (downloadTask, fallbackMessage = '下载失败') => {
    const runDownloadTask = async () => {
      try {
        await downloadTask();
      } catch (err) {
        message.error(err.response?.data?.message || fallbackMessage);
      }
    };

    const remarks = versionRemarks.filter((remark) => String(remark.content || '').trim());
    if (!hasVersionRemarksForDownload || remarks.length === 0) {
      await runDownloadTask();
      return;
    }

    Modal.confirm({
      title: `版本备注 - ${isMarketVersion ? (marketInfo?.productModel || productDisplayName) : productDisplayName} ${displayVersionTitle}`,
      icon: <ExclamationCircleOutlined />,
      width: 640,
      okText: '继续下载',
      cancelText: '取消',
      content: (
        <div className="download-remark-modal">
          <Text type="secondary">此版本有 {remarks.length} 条备注，请确认后继续下载。</Text>
          <div className="download-remark-list">
            {remarks.map((remark, index) => (
              <div className="download-remark-item" key={remark.id || `${remark.created_at}-${index}`}>
                <div className="download-remark-meta">
                  <Text strong>{remark.created_by_name || remark.created_by_email || '未知用户'}</Text>
                  <Text type="secondary">{dayjs(remark.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                </div>
                <div className="download-remark-content">{remark.content}</div>
              </div>
            ))}
          </div>
        </div>
      ),
      onOk: runDownloadTask,
    });
  };

  const handleVersionFileDownload = async (downloadTask) => {
    await showVersionRemarksBeforeDownload(downloadTask);
  };

  // 按类型分组功能
  const featureGroups = {
    new_feature: { label: '新增功能', color: '#1677ff', icon: <CheckCircleOutlined /> },
    improvement: { label: '改进优化', color: '#722ed1', icon: <EditOutlined /> },
    bugfix: { label: '问题修复', color: '#fa8c16', icon: <WarningOutlined /> },
  };

  const renderChangeActions = () => (
    <Space size={8} className="version-change-actions">
      <Button
        size="small"
        icon={changesCollapsed ? <DownOutlined /> : <UpOutlined />}
        onClick={() => setChangesCollapsed(prev => !prev)}
      >
        {changesCollapsed ? '展开' : '收起'}
      </Button>
    </Space>
  );

  const renderDailyDeltaActions = () => (
    <Space size={8} className="version-change-actions">
      <Button
        size="small"
        icon={dailyDeltaCollapsed ? <DownOutlined /> : <UpOutlined />}
        onClick={() => setDailyDeltaCollapsed(prev => !prev)}
      >
        {dailyDeltaCollapsed ? '展开' : '收起'}
      </Button>
    </Space>
  );

  const renderComponentTitle = (count) => (
    <Space size={8}>
      <span>固件组件版本</span>
      <Tag color="blue">{count} 个</Tag>
    </Space>
  );

  const renderDailyTextQualityAlert = (quality) => {
    if (!quality?.sourceCorrupted) return null;
    const total = quality.totalEntries || 0;
    const corrupted = quality.corruptedEntries || 0;
    return (
      <Alert
        className="version-note-quality-alert"
        type="warning"
        showIcon
        message="原始发布说明存在编码异常"
        description={`检测到 ${corrupted}/${total} 条变更在原始 ReleaseNote 中已经包含问号乱码，页面已隐藏问号片段；原始中文无法自动恢复。`}
      />
    );
  };

  const renderDailySectionRecords = (sections, { showUnchanged = true } = {}) => (
    sections.map(section => {
      if (section.unchanged) {
        if (!showUnchanged) return null;
        return (
          <div key={section.name} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <CheckCircleOutlined />
              <Text strong style={{ color: '#1677ff', fontSize: 15 }}>{section.name}</Text>
              <Tag>无变更</Tag>
            </div>
            <Text type="secondary">版本内容无变更</Text>
          </div>
        );
      }
      if (!section.entries || section.entries.length === 0) return null;
      return (
        <div key={section.name} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <CheckCircleOutlined />
            <Text strong style={{ color: '#1677ff', fontSize: 15 }}>{section.name}</Text>
            <Tag>{section.entries.length}</Tag>
          </div>
          <Timeline
            items={section.entries.map(e => ({
              color: '#1677ff',
              children: (
                <div className="version-change-entry">
                  <Text className="version-change-text" style={{ whiteSpace: 'pre-wrap' }}>{e.message}</Text>
                </div>
              ),
            }))}
          />
        </div>
      );
    })
  );

  const renderDailyDeltaRecords = () => (
    <div className="version-section-body version-change-section">
      {dailyNote.loading ? (
        <Spin size="small" tip="读取发布说明中..." />
      ) : dailyNote.error ? (
        <Text type="secondary">暂无本次新增变更</Text>
      ) : dailyDeltaCount === 0 ? (
        <Text type="secondary">
          {dailyDelta?.hasPrevious ? '相对上一每日版本暂无新增变更。' : '未找到上一每日版本，暂无可对比的新增变更。'}
        </Text>
      ) : dailyDeltaCollapsed ? (
        <div className="version-change-collapsed">
          <Text type="secondary" className="version-change-collapsed-meta">
            已折叠，共 {dailyDeltaCount} 条。
            {dailyDelta?.previous ? ` 对比上一每日版本 v${dailyDelta.previous.version}` : ''}
          </Text>
        </div>
      ) : (
        <>
          {renderDailyTextQualityAlert(dailyTextQuality)}
          {dailyDelta?.previous && (
            <Text type="secondary" className="version-change-collapsed-meta">
              对比上一每日版本 v{dailyDelta.previous.version}
            </Text>
          )}
          {renderDailySectionRecords(dailyDeltaSections, { showUnchanged: false })}
        </>
      )}
    </div>
  );

  const renderDailyFullChangeRecords = () => (
    <div className="version-section-body version-change-section">
      {changesCollapsed ? (
        <div className="version-change-collapsed">
          <Text type="secondary" className="version-change-collapsed-meta">
            {changeRecordCount ? `已折叠，共 ${changeRecordCount} 条。` : '暂无可展开的变更记录。'}
          </Text>
        </div>
      ) : dailyNote.loading ? (
        <Spin size="small" tip="读取发布说明中..." />
      ) : dailyNote.error || !dailyNote.sections || dailyNote.sections.length === 0 ? (
        <Text type="secondary">暂无变更记录</Text>
      ) : (
        renderDailySectionRecords(dailyNote.sections)
      )}
    </div>
  );

  const renderChangeRecords = () => (
    <div className="version-section-body version-change-section">
      {changesCollapsed ? (
        <div className="version-change-collapsed">
          <Text type="secondary" className="version-change-collapsed-meta">
            {data.features.length ? `已折叠，共 ${data.features.length} 条。` : '暂无可展开的变更记录。'}
          </Text>
        </div>
      ) : data.features.length === 0 ? (
        <Text type="secondary">暂无变更记录</Text>
      ) : (
        Object.entries(featureGroups).map(([type, config]) => {
          const items = data.features.filter(f => f.type === type);
          if (items.length === 0) return null;
          return (
            <div key={type} style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                {config.icon}
                <Text strong style={{ color: config.color, fontSize: 15 }}>{config.label}</Text>
                <Tag>{items.length}</Tag>
              </div>
              <Timeline
                items={groupFeaturesByIssue(items).map(group => ({
                  color: config.color,
                  children: (
                    <div className="feature-change-card">
                      {group.issueId && <Tag color={config.color}>{group.issueId}</Tag>}
                      <div className="feature-change-items">
                        {group.items.map((f, index) => (
                          <div className="feature-change-item" key={f.id || `${group.issueId}-${index}`}>
                            {group.items.length > 1 && <span className="feature-change-index">{index + 1}</span>}
                            <Text strong className="feature-change-text">{f.text || f.title}</Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                }))}
              />
            </div>
          );
        })
      )}
    </div>
  );

  const renderVersionRemarks = () => (
    <Card className="content-card version-remark-card" title={`版本备注 (${versionRemarks.length})`}>
      <div className="version-remark-form">
        <TextArea
          value={remarkText}
          onChange={(e) => setRemarkText(e.target.value)}
          placeholder="填写版本备注"
          autoSize={{ minRows: 3, maxRows: 6 }}
          maxLength={2000}
          showCount
        />
        <div className="version-remark-form-actions">
          <Button
            type="primary"
            className="version-remark-submit"
            onClick={handleAddRemark}
            loading={remarkSubmitting}
            disabled={!remarkText.trim()}
          >
            添加备注
          </Button>
        </div>
      </div>
      {versionRemarks.length === 0 ? (
        <Text type="secondary">暂无备注</Text>
      ) : (
        <div className="version-remark-list">
          {versionRemarks.map(remark => {
            const author = remark.created_by_name || remark.created_by_email || '未知用户';
            return (
              <div className="version-remark-item" key={remark.id}>
                <div className="version-remark-meta">
                  <Space size={8} wrap className="version-remark-meta-main">
                    <Text strong className="version-remark-author">{author}</Text>
                    {remark.created_by_email && remark.created_by_email !== author && (
                      <Text type="secondary">{remark.created_by_email}</Text>
                    )}
                    <Text type="secondary">{dayjs(remark.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                  </Space>
                  {canDeleteRemark(remark) && (
                    <Popconfirm
                      title="删除备注"
                      description="确认删除这条备注吗？"
                      okText="删除"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDeleteRemark(remark.id)}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={remarkDeletingId === remark.id}
                      />
                    </Popconfirm>
                  )}
                </div>
                <Paragraph className="version-remark-content">{remark.content}</Paragraph>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  return (
    <div className="page-shell version-detail-shell">
      {/* 返回按钮 */}
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16, paddingLeft: 0 }}>
        返回版本列表
      </Button>

      {/* 版本头部 */}
      <Card className="content-card version-detail-hero" style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space align="center" size={16}>
              <Title level={2} style={{ margin: 0, fontFamily: 'monospace' }}>
                {displayVersionTitle}
              </Title>
              {(data.version_type !== 'daily' || ['pre_released', 'released'].includes(data.status)) && (
                <StatusBadge status={data.status} />
              )}
              {isMarketVersion ? <Tag color="blue">市场归档</Tag> : <ReleaseTypeBadge type={data.release_type} />}
              <Tag>{isMarketVersion ? (marketInfo?.productModel || '市场版本') : productDisplayName}</Tag>
            </Space>
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 15 }}>
              {data.description}
            </Paragraph>
          </Col>
          {isAdmin && data.version_type !== 'daily' && (
            <Col>
              <Space>
                <Text type="secondary">状态：</Text>
                <Select
                  value={data.status}
                  onChange={handleStatusChange}
                  style={{ width: 120 }}
                  options={[
                    { label: '草稿', value: 'draft' },
                    { label: '已发布', value: 'released' },
                  ]}
                />
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      <div className="version-detail-flow">
        {/* 变更日志 */}
        <div className="version-detail-main version-detail-section-stack">
          {isMarketVersion && marketInfo && (
                <Card className="content-card" title="市场归档信息" style={{ marginBottom: 16 }}>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="归档版本">
                  <Text style={{ wordBreak: 'break-all' }}>{renderMarketValue(marketInfo.archiveVersion || data.version)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="客户">{renderMarketValue(marketInfo.customer)}</Descriptions.Item>
                <Descriptions.Item label="FAE">{renderMarketValue(marketInfo.fae)}</Descriptions.Item>
                <Descriptions.Item label="终端客户">{renderMarketValue(marketInfo.terminalCustomer)}</Descriptions.Item>
                <Descriptions.Item label="部门">{renderMarketValue(marketInfo.department)}</Descriptions.Item>
                <Descriptions.Item label="项目">{renderMarketValue(marketInfo.project)}</Descriptions.Item>
                <Descriptions.Item label="产品型号">{renderMarketValue(marketInfo.productModel)}</Descriptions.Item>
                <Descriptions.Item label="归档日期">{renderMarketValue(marketInfo.archiveDate)}</Descriptions.Item>
                <Descriptions.Item label="NAS路径">{renderMarketValue(marketInfo.nasPath)}</Descriptions.Item>
                <Descriptions.Item label="文件分类">{renderMarketValue(marketInfo.category)}</Descriptions.Item>
                <Descriptions.Item label="归档表位置">
                  {marketInfo.sheetName ? `${marketInfo.sheetName} 第 ${marketInfo.rowNumber} 行` : '--'}
                </Descriptions.Item>
                <Descriptions.Item label="备注">
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{renderMarketValue(marketInfo.remark)}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
          {isMarketVersion && renderVersionRemarks()}

          {!isMarketVersion && data.version_type === 'daily' ? (
            <>
              <Card className="content-card version-change-section" title={`本次新增变更 (${dailyDeltaCount})`} extra={renderDailyDeltaActions()}>
                {renderDailyDeltaRecords()}
              </Card>
              <Card className="content-card version-change-section" title={`完整变更记录 (${changeRecordCount || '发布说明'})`} extra={renderChangeActions()}>
                {renderDailyFullChangeRecords()}
              </Card>
              {renderVersionRemarks()}
            </>
          ) : !isMarketVersion && (
            <>
              <Card className="content-card version-change-section" title={changeRecordTitle} extra={renderChangeActions()}>
                {renderChangeRecords()}
              </Card>
              {renderVersionRemarks()}
            </>
          )}

        </div>

        {/* 元数据侧边栏 */}
        <div className="version-overview-grid">
          <Card className="content-card version-overview-card version-summary-card" title="版本摘要" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="产品名称">{data.product_name}</Descriptions.Item>
              <Descriptions.Item label="产品型号"><Tag>{isMarketVersion ? (marketInfo?.productModel || productDisplayName) : productDisplayName}</Tag></Descriptions.Item>
              {!isMarketVersion && (
                <Descriptions.Item label="发布类型">
                  <ReleaseTypeBadge type={data.release_type} />
                </Descriptions.Item>
              )}
              {(data.version_type !== 'daily' || ['pre_released', 'released'].includes(data.status)) && (
                <Descriptions.Item label="状态">
                  <StatusBadge status={data.status} />
                </Descriptions.Item>
              )}
              {isMarketVersion && marketInfo ? (
                <>
                  <Descriptions.Item label="客户">{marketInfo.customer || '-'}</Descriptions.Item>
                  <Descriptions.Item label="FAE">{marketInfo.fae || '-'}</Descriptions.Item>
                  <Descriptions.Item label="归档日期">{marketInfo.archiveDate || '-'}</Descriptions.Item>
                </>
              ) : (
                <>
                  <Descriptions.Item label="上传者">{data.uploaded_by}</Descriptions.Item>
                  <Descriptions.Item label="上传时间">
                    {dayjs(data.created_at).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="更新时间">
                {dayjs(data.updated_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              {data.source_version && (
                <Descriptions.Item label="来源日常版本">
                  <Tag color="orange">{data.source_version}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
            <div className="version-file-actions version-summary-actions">
              <Button
                icon={<SendOutlined />}
                size="small"
                block
                loading={summaryFeishuSending}
                onClick={handleShareVersionSummaryToFeishu}
              >
                发送到飞书
              </Button>
            </div>
          </Card>

              <Card className="content-card version-overview-card" title="文件信息" size="small">
                {displayFileName ? (
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="文件名">
                      <Text strong style={{ fontSize: 14, wordBreak: 'break-all' }}>{displayFileName}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="文件大小">{formatFileSize(displayFileSize)}</Descriptions.Item>
                    {data.file_hash && (
                      <Descriptions.Item label="MD5">
                        <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                      {data.file_hash}
                    </Text>
                  </Descriptions.Item>
                )}
                {welzekViewerAttachment && (
                  <Descriptions.Item label="WelzekViewer">
                    <Text strong style={{ fontSize: 14, wordBreak: 'break-all' }}>
                      {welzekViewerAttachment.file_name}
                    </Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item>
                  <div className="version-file-actions">
                    <Button
                      type="primary"
                      className="version-download-button"
                      icon={<DownloadOutlined />}
                      size="middle"
                      block
                      onClick={() => handleVersionFileDownload(
                        () => (hasLocalFile ? downloadVersionFile(data.id) : downloadGitLabVersionFile(data.id))
                      )}
                    >
                      下载文件 ({formatFileSize(displayFileSize)})
                    </Button>
                  </div>
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <Text type="secondary">未上传文件</Text>
                )}
            {welzekViewerAttachment && (
              <div className="version-file-actions version-file-actions-extra">
                <Button
                  type="primary"
                  className="version-download-button"
                  icon={<DownloadOutlined />}
                  size="middle"
                  block
                  onClick={() => handleVersionFileDownload(() => downloadWelzekViewerAttachment(
                    data.id,
                    welzekViewerAttachment.file_name
                  ))}
                >
                  下载 WelzekViewer ({formatFileSize(welzekViewerAttachment.file_size)})
                </Button>
              </div>
            )}
          </Card>

          {/* 版本组件信息 (DSP/MCU/DRIVER等) */}
          {!isMarketVersion && data.version_info && (() => {
            const entries = getFirmwareVersionEntries(data.version_info);
            if (entries.length === 0) return null;
            return (
              <Card className="content-card version-overview-card" title={renderComponentTitle(entries.length)} size="small">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {entries.map(entry => (
                    <Tag key={entry.key} color={entry.color} style={{ margin: 0 }}>
                      {entry.label}: <Text strong style={{ fontFamily: 'monospace' }}>{entry.value}</Text>
                    </Tag>
                  ))}
                </div>
              </Card>
            );
          })()}

              {!isMarketVersion && (
                <Card className="content-card version-overview-card" title="统计" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="变更记录">{statsChangeCount} 项</Descriptions.Item>
                  <Descriptions.Item label="新增功能">
                    {statsTypeCounts.new_feature || 0} 项
                  </Descriptions.Item>
                  <Descriptions.Item label="改进优化">
                    {statsTypeCounts.improvement || 0} 项
                  </Descriptions.Item>
                  <Descriptions.Item label="问题修复">
                    {statsTypeCounts.bugfix || 0} 项
                  </Descriptions.Item>
                </Descriptions>
              </Card>
          )}
        </div>
      </div>
    </div>
  );
}
